import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { siteConfig, navLinks, categories } from '@/mocks/siteData';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const guideArticles = [
    { label: 'Beginner Guide', slug: 'vigorbuy-spreadsheet-beginner-guide' },
    { label: 'Shoes Guide', slug: 'vigorbuy-spreadsheet-shoes-guide' },
    { label: 'Jackets Guide', slug: 'vigorbuy-spreadsheet-jackets-guide' },
    { label: 'Bags Guide', slug: 'vigorbuy-spreadsheet-bags-guide' },
    { label: 'Hoodies Guide', slug: 'vigorbuy-spreadsheet-hoodies-guide' },
    { label: 'T-Shirts Guide', slug: 'vigorbuy-spreadsheet-tshirts-guide' },
    { label: 'Accessories Guide', slug: 'vigorbuy-spreadsheet-accessories-guide' },
    { label: 'Safety Guide', slug: 'vigorbuy-spreadsheet-safety-guide' },
    { label: 'QC Photo Guide', slug: 'vigorbuy-spreadsheet-qc-photo-guide' },
    { label: 'Price Guide', slug: 'vigorbuy-spreadsheet-price-comparison-guide' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-4 md:px-6 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2 group" onClick={() => setMobileOpen(false)}>
          <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
            <i className="ri-table-line text-background-50 text-lg"></i>
          </div>
          <span className="font-heading font-bold text-lg text-foreground-900 whitespace-nowrap">
            Vigorbuy<span className="text-primary-500">Spread</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-6">
          {navLinks.map((link) =>
            link.href.startsWith('#') ? (
              <a
                key={link.label}
                href={link.href}
                className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap"
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.label}
                to={link.href}
                className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap"
              >
                {link.label}
              </Link>
            )
          )}

          <div className="relative">
            <button
              onClick={() => setGuideOpen(!guideOpen)}
              className="flex items-center gap-1.5 text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap"
            >
              More Guides
              <i className={`ri-arrow-down-s-line text-xs transition-transform ${guideOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {guideOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setGuideOpen(false)}></div>
                <div className="absolute top-full right-0 mt-2 w-56 bg-background-50 border border-background-200/70 rounded-xl shadow-lg z-20 py-2">
                  {guideArticles.map((ga) => (
                    <Link
                      key={ga.slug}
                      to={`/article/${ga.slug}`}
                      onClick={() => setGuideOpen(false)}
                      className="block px-4 py-2 text-sm text-foreground-600 hover:bg-background-100 hover:text-foreground-900 transition-colors whitespace-nowrap"
                    >
                      {ga.label}
                    </Link>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={siteConfig.mainSite}
            target="_blank"
            rel="noopener nofollow"
            className="px-4 py-2 text-sm font-medium text-foreground-700 border border-background-300 rounded-full hover:bg-background-100 transition-all whitespace-nowrap"
          >
            Vigorbuy Spreadsheet
          </a>
          <a
            href={siteConfig.mainSite}
            target="_blank"
            rel="noopener nofollow"
            className="px-5 py-2 text-sm font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-all whitespace-nowrap"
          >
            Buy It Now
          </a>
        </div>

        <button
          className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl text-foreground-800`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 px-4 py-4 flex flex-col max-h-[70vh] overflow-y-auto">
          {navLinks.map((link) =>
            link.href.startsWith('#') ? (
              <a
                key={link.label}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors py-2.5"
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.label}
                to={link.href}
                onClick={() => setMobileOpen(false)}
                className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors py-2.5"
              >
                {link.label}
              </Link>
            )
          )}

          <div className="mt-2 pt-3 border-t border-background-200/60">
            <span className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wider px-1">Shopping Guides</span>
            <div className="mt-2 space-y-0.5">
              {guideArticles.map((ga) => (
                <Link
                  key={ga.slug}
                  to={`/article/${ga.slug}`}
                  onClick={() => setMobileOpen(false)}
                  className="block py-2 px-2 text-sm text-foreground-500 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap"
                >
                  {ga.label}
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-3 pt-3 border-t border-background-200/60">
            <span className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wider px-1">Shop Categories</span>
            <div className="mt-2 grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 py-2 px-2 text-sm text-foreground-500 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap"
                >
                  <i className={`${cat.icon} text-xs text-primary-500`}></i>
                  {cat.name}
                </a>
              ))}
            </div>
          </div>

          <div className="flex gap-2 pt-4 mt-3 border-t border-background-200/60">
            <a
              href={siteConfig.mainSite}
              target="_blank"
              rel="noopener nofollow"
              className="flex-1 px-3 py-2.5 text-sm font-medium text-center text-foreground-700 border border-background-300 rounded-full hover:bg-background-100 transition-all whitespace-nowrap"
            >
              Vigorbuy Spreadsheet
            </a>
            <a
              href={siteConfig.mainSite}
              target="_blank"
              rel="noopener nofollow"
              className="flex-1 px-3 py-2.5 text-sm font-semibold text-center bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-all whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}